import { GetUserRole } from "@/appComponents/Blocks/Modals/ChartExplorerModal/PlanExplorer/PlanExplorer";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  enumFeaturesList,
  enumPlanRoles,
  enumPlanStatus,
  kAdminUser,
  kUserEditRoles
} from "@coreapp/constants";
import { getFeatureAccess } from "./Misc";

type PlanObj = {
  owner: string;
  status: "Active" | "Archived" | "Locked";
  users: { name: string; role: "viewer" | "collaborator" | "owner" }[];
};

type Options = {
  plan?: PlanObj;
  useCurrentPlan?: boolean;
};

/*
 *
 *  App admins have access to (on ALL plans):
 *   - Administrative operations
 *
 *  Plan collaborators have access to:
 *   - Collaborative operations
 *
 *   Plan owners have access to all operations on their plans
 *
 *  Collaborative operations:
 *   - Manage metrics
 *   - Manage add't fields
 *   - Share plan
 *   - Duplicate plan
 *   - About plan
 *   - Sync plan
 *
 *  Owner operations:
 *   + Collaborative operations
 *   - Rename plan
 *   - Manage status
 *   - Delete plan
 *
 *  Administrative operations:
 *   - About plan
 *   - Share plan
 *   - Duplicate plan
 *   - Rename plan
 *   - Manage status
 *   - Delete plan
 *
 *
 *  Plans can only perform some operations in specific statuses:
 *
 *  Active-only operations:
 *   - Sync plan
 *   - Manage fields
 *   - Manage metrics
 *   - Manage status
 */

export const getPlanPermissions = (options: Options) => {
  const wsMgr = GetWSMgr();
  const userInfo = wsMgr?.GetUserInfo();
  const userID = userInfo?.GetUserName();

  /* App-level permissions */
  const appRole = userInfo?.GetUserRole();
  const isAppAdmin = appRole === kAdminUser;

  /* Plan-level permissions */
  const planRole = (() => {
    if (options.useCurrentPlan) {
      const planMgr = wsMgr?.GetPlanMgr();
      return planMgr?.GetPlanRole();
    }

    return GetUserRole(options.plan?.users ?? [], userID);
  })();

  const isPlanCollaborator = kUserEditRoles.includes(planRole);
  const isPlanOwner = planRole === enumPlanRoles.owner;
  const isPlanViewer = planRole === enumPlanRoles.viewer;

  const planStatus = (() => {
    if (options.useCurrentPlan) {
      const planMgr = wsMgr?.GetPlanMgr();
      return planMgr?.GetPlanStatus();
    }
    return options.plan?.status;
  })();

  const isPlanActive = planStatus === enumPlanStatus.active;

  return {
    canDelete: isAppAdmin || isPlanOwner,
    canEdit: isPlanActive && isPlanCollaborator,
    canRename: isPlanActive && (isAppAdmin || isPlanCollaborator),
    canDuplicate: isAppAdmin || isPlanCollaborator,
    canShare: isAppAdmin || isPlanCollaborator,
    canSync: isPlanActive && isPlanCollaborator,
    canManageFields: isPlanActive && isPlanCollaborator,
    canManageMetrics:
      isPlanActive && isPlanCollaborator && getFeatureAccess(enumFeaturesList.planMetrics),
    canManageStatus: isAppAdmin || isPlanCollaborator,
    canViewAbout: isAppAdmin || isPlanCollaborator,
    canSubmitPlans: isAppAdmin || isPlanCollaborator || isPlanOwner,
    canReviewPlans: isAppAdmin || isPlanCollaborator || isPlanOwner || isPlanViewer,
    planStatus,
    isPlanCollaborator
  };
};
